import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Button, Collapse, InputNumber, Space, Table } from "antd"
import { ColumnsType } from "antd/lib/table"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import { BonusSpendTierStatus } from "../store/postbacks.store"
import { GamePlatformType } from "../store/account-games-BuffPlay.store"

const { Panel } = Collapse

type Props = {
  accountId: string
  gameId: string
  platform: GamePlatformType
}

export const AccountBonusSpendSection: FC<Props> = observer(({ accountId, gameId, platform }) => {
  const { dataStore: { accountPostbacksStore } } = useStore()
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [spendInput, setSpendInput] = useState<number | null>(null)
  const status = accountPostbacksStore.getBonusSpendStatus(gameId, platform)
  const cumulativeAmount = status?.cumulativeAmount ?? 0
  const tiers = status?.tiers ?? []

  const refetch = useCallback(async() => {
    setLoading(true)
    try {
      await accountPostbacksStore.loadBonusSpendStatus(accountId, gameId, platform)
    } catch (err: any) {
      notifications.error(err?.message || "Failed to load bonus spend status")
    } finally {
      setLoading(false)
    }
  }, [accountId, gameId, platform, accountPostbacksStore])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const onAddSpend = async() => {
    if (spendInput == null || !Number.isFinite(spendInput) || spendInput <= 0) {
      notifications.error("Spend amount must be a positive number")

      return
    }
    setBusy(true)
    try {
      // Round to 2 decimal places before sending.
      const amount = Math.round(spendInput * 100) / 100
      await accountPostbacksStore.applySpend(accountId, gameId, amount, platform)
      notifications.success(`Recorded $${amount.toFixed(2)} spend (${platform})`)
      setSpendInput(null)
      await refetch()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to apply spend")
    } finally {
      setBusy(false)
    }
  }

  const onClaimTier = async(tier: BonusSpendTierStatus) => {
    setBusy(true)
    try {
      await accountPostbacksStore.claimBonusSpendMission(accountId, gameId, tier.id)
      notifications.success(`Claimed tier ${tier.level}`)
      await refetch()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to claim tier")
    } finally {
      setBusy(false)
    }
  }

  const columns: ColumnsType<BonusSpendTierStatus> = [
    { title: "Tier #", dataIndex: "level", key: "level", width: 80 },
    {
      title: "Spend Threshold",
      dataIndex: "spendThresholdUsd",
      key: "spendThresholdUsd",
      width: 140,
      render: (v: number) => `$${v.toFixed(2)}`,
    },
    {
      title: "Reward",
      dataIndex: "rewardUsd",
      key: "rewardUsd",
      width: 100,
      render: (v: number) => `$${v.toFixed(2)}`,
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, row) => (row.isClaimed ? "Claimed" : row.isClaimable ? "Claimable" : "Locked"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, row) => {
        const disabled = busy || row.isClaimed || !row.isClaimable

        return (
          <Button size="small" type="primary" disabled={disabled} onClick={() => onClaimTier(row)}>
            Claim Tier
          </Button>
        )
      },
    },
  ]

  return (
    <Collapse style={{ marginTop: 20 }} defaultActiveKey={["bonus-spend"]}>
      <Panel key="bonus-spend" header={`Bonus Spend (${platform}) — $${cumulativeAmount.toFixed(2)} cumulative`}>
        <Space style={{ marginBottom: 12 }}>
          <InputNumber
            min={0.01}
            step={0.01}
            precision={2}
            value={spendInput ?? undefined}
            placeholder="USD"
            onChange={value => setSpendInput(value == null ? null : Number(value))}
            disabled={busy}
          />
          <Button type="primary" disabled={busy || spendInput == null || spendInput <= 0} onClick={onAddSpend}>
            Add Spend
          </Button>
        </Space>
        <Table<BonusSpendTierStatus>
          dataSource={tiers}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={loading}
          bordered
          size="small"
        />
      </Panel>
    </Collapse>
  )
})
